import { Component, inject, Injector, runInInjectionContext } from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { MatButton } from "@angular/material/button";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { RouterLink } from "@angular/router";
import { UserDetailsService } from "@shared/core/services/user-details.service";
import { combineLatestWith, defer, filter, first, map, of, shareReplay, startWith, Subject, switchMap, tap } from "rxjs";
import { AsyncPipe } from "@angular/common";
import { AuthService } from "@auth/interfaces/IAuthService";
import { DialogService, UbbTranslatePipe } from "@ubisolutions/base";
import { ConfirmDialogComponent } from "@ubisolutions/base/components";
import { notNull } from "@ubisolutions/base/utils";
import { UserRolesService, GraphqlService } from "@ubisolutions/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ImpersonateGraphql } from "@shared/base/components/topbar-user-button/topbar-user-button.graphql";

@Component({
    selector: "app-topbar-user-button",
    imports: [
        MatIcon,
        MatMenu,
        MatMenuTrigger,
        MatMenuItem,
        AsyncPipe,
        MatButton,
        UbbTranslatePipe,
        RouterLink,
    ],
    templateUrl: "./topbar-user-button.component.html",
    styleUrl: "./topbar-user-button.component.scss",
})
export class TopbarUserButtonComponent {
    private readonly userDetailsService = inject(UserDetailsService);
    private readonly authService = inject(AuthService);
    private readonly roleService = inject(UserRolesService);
    private readonly dialog = inject(DialogService);
    private readonly graphqlService = inject(GraphqlService);
    private readonly injector = inject(Injector);

    protected readonly logout$ = new Subject<void>();
    protected readonly impersonate$ = new Subject<void>();
    protected readonly stopImpersonate$ = new Subject<void>();

    protected canImpersonate$ = this.roleService.canI("CREATE", "Impersonate").pipe(
        startWith(false),
    );

    protected userName$ = this.userDetailsService.getUserDetails().pipe(
        map(user => user.userName),
    );

    private impersonatingQuery$ = this.canImpersonate$.pipe(
        filter(can => can),
        switchMap(() => this.graphqlService.query(ImpersonateGraphql.currentImpersonate).fetchOnce({})),
        shareReplay(1),
    );

    protected impersonating$ = this.impersonatingQuery$.pipe(
        map(result => result.myImpersonate),
        map(result => result != null && result.active),
        startWith(false),
        shareReplay(1),
    );

    protected impersonatingName$ = this.impersonatingQuery$.pipe(
        map(response => response.myImpersonate),
        filter(notNull),
        map((result) => {
            return [result.client.name, result.subTenant?.name ?? "All"].filter(notNull)
                .join(" | ");
        }),
        startWith("All"),
    );

    // Loaded on first use: the create dialog and the impersonation form pull in
    // @ubisolutions/core/dialogs and @ubisolutions/base/form, which the app shell
    // (this button included) must not load at startup.
    private readonly impersonateDialog$ = defer(() => Promise.all([
        import("@ubisolutions/core/dialogs"),
        import("@shared/base/components/topbar-user-button/topbar-user-button.form-definition"),
    ])).pipe(
        map(([{ CreateDialogComponent }, { ImpersonateFormDefinition }]) => ({
            component: CreateDialogComponent,
            form: runInInjectionContext(this.injector, () => ImpersonateFormDefinition()),
        })),
        shareReplay(1),
    );

    private impersonateSubscription = this.impersonate$.pipe(
        takeUntilDestroyed(),
        switchMap(() => this.impersonateDialog$.pipe(first())),
        switchMap(({ component, form }) => this.dialog.open(component, {
            data: {
                typename: "Impersonate",
                form,
            },
        }).afterClosed()),
        filter(result => !!result?.success),
    ).subscribe({
        next: () => location.reload(),
    });

    private stopImpersonateSubscription = this.stopImpersonate$.pipe(
        takeUntilDestroyed(),
        switchMap(_ => this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: "business.components.topbar-user-button.confirm-dialog.title",
                description: "business.components.topbar-user-button.confirm-dialog.description",
            },
        }).afterClosed()),
        map(value => !!value?.success),
        filter(value => !!value),
        switchMap(() => this.graphqlService.mutate(ImpersonateGraphql.stopImpersonate, {})),
    ).subscribe({
        next: () => location.reload(),
    });

    private logoutSubscription = this.logout$.pipe(
        combineLatestWith(this.impersonating$),
        takeUntilDestroyed(),
        switchMap(([_, impersonating]) => {
            if (impersonating != null && impersonating) return this.graphqlService.mutate(ImpersonateGraphql.stopImpersonate, {});
            return of({});
        }),
    ).subscribe({
        next: () => this.authService.logOut(),
    });
}
